import React, { useState, useEffect, useCallback } from 'react';
import { 
  Mail, 
  Send, 
  Eye, 
  MousePointer,
  Calendar,
  Search,
  RefreshCw,
  Loader2,
  ExternalLink,
  CheckCircle,
  Clock,
  Cloud,
  CloudOff,
  ChevronLeft,
  ChevronRight,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { mauticApi } from '../lib/api';

export const SentEmails = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [isMauticConnected, setIsMauticConnected] = useState(false);
  const [totalEmails, setTotalEmails] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const pageSize = 15;

  const fetchEmails = useCallback(async (page = 0) => {
    try {
      setLoading(true);
      const response = await mauticApi.getEmails({ limit: 50, start: page * pageSize });
      
      setIsMauticConnected(!response.data.simulated);
      setEmails(response.data.emails || []);
      setTotalEmails(response.data.total || response.data.emails?.length || 0);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Error al cargar los emails');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchEmails(currentPage);
  };

  // Filter and search emails
  const filteredEmails = emails.filter(email => {
    const matchesSearch = 
      email.name?.toLowerCase().includes(search.toLowerCase()) ||
      email.subject?.toLowerCase().includes(search.toLowerCase());
    
    const matchesFilter = 
      filterType === 'all' ||
      (filterType === 'sent' && email.sentCount > 0) ||
      (filterType === 'draft' && email.sentCount === 0);
    
    return matchesSearch && matchesFilter;
  });

  // Calculate stats
  const totalSent = emails.reduce((sum, e) => sum + (e.sentCount || 0), 0);
  const totalRead = emails.reduce((sum, e) => sum + (e.readCount || 0), 0);
  const avgOpenRate = totalSent > 0 ? ((totalRead / totalSent) * 100).toFixed(1) : 0;

  const totalPages = Math.ceil(filteredEmails.length / pageSize);
  const paginatedEmails = filteredEmails.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const getOpenRate = (email) => {
    if (!email.sentCount || email.sentCount === 0) return 0;
    return ((email.readCount / email.sentCount) * 100).toFixed(1);
  };

  const getStatusBadge = (email) => {
    if (email.sentCount > 0) {
      return <Badge className="bg-green-100 text-green-700">Enviado</Badge>;
    }
    return <Badge variant="outline">Borrador</Badge>;
  };

  return (
    <div className="p-8 animate-fade-in" data-testid="sent-emails-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Emails Enviados</h1>
            {isMauticConnected ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <Cloud className="w-3 h-3 mr-1" />
                Mautic conectado
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-50 text-slate-500">
                <CloudOff className="w-3 h-3 mr-1" />
                Datos simulados
              </Badge>
            )}
          </div>
          <p className="text-slate-500 mt-1">
            Historial de campañas de email enviadas desde Mautic
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-emails-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Emails</p>
                <p className="text-3xl font-bold text-slate-900">{totalEmails}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Enviados</p>
                <p className="text-3xl font-bold text-slate-900">{totalSent}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Send className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Abiertos</p>
                <p className="text-3xl font-bold text-slate-900">{totalRead}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Tasa Apertura</p>
                <p className="text-3xl font-bold text-slate-900">{avgOpenRate}%</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <MousePointer className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre o asunto..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(0); }}
            className="pl-10"
            data-testid="search-emails"
          />
        </div>
        <Select value={filterType} onValueChange={(v) => { setFilterType(v); setCurrentPage(0); }}>
          <SelectTrigger className="w-40" data-testid="filter-emails">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="sent">Enviados</SelectItem>
            <SelectItem value="draft">Borradores</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Emails Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
              <p className="text-slate-500 mt-2">Cargando emails de Mautic...</p>
            </div>
          ) : paginatedEmails.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Asunto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-center">Enviados</TableHead>
                  <TableHead className="text-center">Abiertos</TableHead>
                  <TableHead className="text-center">Tasa Apertura</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedEmails.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="font-medium truncate max-w-[200px]" title={email.name}>
                          {email.name || 'Sin nombre'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="truncate max-w-[250px] block" title={email.subject}>
                        {email.subject || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(email)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold">{email.sentCount || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold">{email.readCount || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant="outline" 
                        className={
                          parseFloat(getOpenRate(email)) >= 30 
                            ? 'bg-green-50 text-green-700 border-green-200' 
                            : parseFloat(getOpenRate(email)) >= 15
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-slate-50'
                        }
                      >
                        {getOpenRate(email)}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No hay emails
              </h3>
              <p className="text-slate-500">
                {search ? 'No se encontraron emails con ese criterio' : 'Los emails enviados aparecerán aquí'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {filteredEmails.length > pageSize && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">
            Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, filteredEmails.length)} de {filteredEmails.length} emails
          </p>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentPage(p => p - 1)}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <span className="text-sm text-slate-500 px-2">
              Página {currentPage + 1} de {totalPages}
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentEmails;
